import { marked } from 'marked';

/**
 * Copies content to clipboard as both rich text (HTML) and plain text.
 * When pasting into rich text editors (Notion, Google Docs, Slack),
 * the HTML version preserves markdown formatting.
 * When pasting into plain text editors, the original markdown is used.
 *
 * @param content - The markdown content to copy
 * @returns Promise that resolves to true if copy succeeded, false otherwise
 */
export async function copyToClipboard(content: string): Promise<boolean> {
  if (!content) {
    return false;
  }

  try {
    // Convert markdown to HTML for rich text pasting
    const html = await marked(content);
    const htmlBlob = new Blob([html], { type: 'text/html' });
    const textBlob = new Blob([content], { type: 'text/plain' });

    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob,
      }),
    ]);

    return true;
  } catch (err) {
    console.error('Failed to copy as rich text:', err);
    // Fallback to plain text copy
    try {
      await navigator.clipboard.writeText(content);
      return true;
    } catch {
      console.error('Failed to copy as plain text');
      return false;
    }
  }
}
